import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheck } from 'lucide-react';
import { PASSWORD_MIN_LENGTH, changePasswordSchema, forgotPasswordSchema, resetPasswordSchema } from '@blimo/shared';
import { Form, FormRootError, InputField, applyProblemToForm } from '../components/form.jsx';
import { Button } from '../components/button.jsx';
import { Alert, AlertDescription } from '../components/alert.jsx';

const PASSWORD_HINT = `Mindestens ${PASSWORD_MIN_LENGTH} Zeichen. Bekannte, in Datenlecks aufgetauchte Passwörter werden abgelehnt.`;

/** @param {{ onSubmit: (values: { email: string }) => Promise<unknown> }} props */
export function ForgotPasswordForm({ onSubmit }) {
  const [sent, setSent] = useState(false);
  const form = useForm({ resolver: zodResolver(forgotPasswordSchema), defaultValues: { email: '' } });

  async function submit(values) {
    try {
      await onSubmit(values);
      setSent(true);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['email'] });
    }
  }

  if (sent) {
    return (
      <Alert variant="success">
        <CircleCheck aria-hidden="true" />
        <AlertDescription>
          Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet. Der Link ist 60 Minuten
          gültig.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-4" noValidate>
        <FormRootError />
        <InputField name="email" label="E-Mail-Adresse" type="email" autoComplete="username" inputMode="email" autoFocus />
        <Button type="submit" loading={form.formState.isSubmitting} className="w-full">
          Link anfordern
        </Button>
      </form>
    </Form>
  );
}

/**
 * @param {{ defaultEmail?: string, onSubmit: (values: { email: string, password: string, password_confirmation: string }) => Promise<unknown> }} props
 */
export function ResetPasswordForm({ defaultEmail = '', onSubmit }) {
  const form = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { email: defaultEmail, password: '', password_confirmation: '' },
  });

  async function submit(values) {
    try {
      await onSubmit(values);
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['email', 'password', 'password_confirmation'] });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-4" noValidate>
        <FormRootError />
        <InputField name="email" label="E-Mail-Adresse" type="email" autoComplete="username" />
        <InputField name="password" label="Neues Passwort" type="password" autoComplete="new-password" description={PASSWORD_HINT} />
        <InputField name="password_confirmation" label="Neues Passwort bestätigen" type="password" autoComplete="new-password" />
        <Button type="submit" loading={form.formState.isSubmitting} className="w-full">
          Passwort speichern
        </Button>
      </form>
    </Form>
  );
}

/**
 * @param {{ onSubmit: (values: { current_password: string, password: string, password_confirmation: string }) => Promise<unknown>, onSuccess?: () => void }} props
 */
export function ChangePasswordForm({ onSubmit, onSuccess }) {
  const form = useForm({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { current_password: '', password: '', password_confirmation: '' },
  });

  async function submit(values) {
    try {
      await onSubmit(values);
      form.reset();
      onSuccess?.();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['current_password', 'password', 'password_confirmation'] });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submit)} className="flex max-w-md flex-col gap-4" noValidate>
        <FormRootError />
        <InputField name="current_password" label="Aktuelles Passwort" type="password" autoComplete="current-password" />
        <InputField name="password" label="Neues Passwort" type="password" autoComplete="new-password" description={PASSWORD_HINT} />
        <InputField name="password_confirmation" label="Neues Passwort bestätigen" type="password" autoComplete="new-password" />
        <Button type="submit" loading={form.formState.isSubmitting} className="self-start">
          Passwort ändern
        </Button>
      </form>
    </Form>
  );
}
